import {ActionList, Banner, Button, Link} from '@primer/react'
import type {Pull} from '../../types/pull'
import styles from './TaskChatInputHeader.module.css'
import {PullRequestButton} from './PullRequestButton'
import {DiffStats} from '@github-ui/diffs/DiffParts'
import {ChevronRightIcon, FileAddedIcon, FileDiffIcon, FileRemovedIcon} from '@primer/octicons-react'
import {useState} from 'react'
import {useBranchAliveUpdates} from '../../hooks/use-branch-alive-updates'
import {useTaskDiffs} from '../../hooks/use-task-diffs'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'

interface TaskChatInputHeaderProps {
  pull?: Pull
  repoNwo: string
  taskId: string
  baseRef: string
  headRef: string
  branchAliveChannel?: string
  isSessionInProgress?: boolean
  onViewDiff?: (filePath?: string) => void
}

export function TaskChatInputHeader({
  pull,
  repoNwo,
  taskId,
  baseRef,
  headRef,
  branchAliveChannel,
  isSessionInProgress = false,
  onViewDiff,
}: TaskChatInputHeaderProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [prError, setPrError] = useState<string | null>(null)
  const {data: diffs, refetch: refetchDiffs} = useTaskDiffs(repoNwo, taskId, baseRef, headRef)
  const hasDiffs = !!diffs && diffs.files.length > 0
  const branchHref = `/${repoNwo}/tree/${encodeURIComponent(headRef)}`
  useBranchAliveUpdates(branchAliveChannel, () => {
    refetchDiffs()
  })

  if (!hasDiffs) return null

  const getPathComponents = (filePath: string) => {
    const lastSlashIndex = filePath.lastIndexOf('/')
    if (lastSlashIndex === -1) {
      return ['', filePath]
    }
    return [filePath.slice(0, lastSlashIndex), filePath.slice(lastSlashIndex + 1)]
  }

  const getFileStatusIcon = (status: string) => {
    switch (status) {
      case 'A':
        return <FileAddedIcon className={styles.fileAdded} />
      case 'D':
      case 'R':
        return <FileRemovedIcon className={styles.fileDeleted} />
      default:
        return <FileDiffIcon className={styles.fileChanged} />
    }
  }

  const handleClick = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.info}>
          <button
            onClick={handleClick}
            type="button"
            className={styles.toggleButton}
            aria-expanded={isDrawerOpen}
            aria-controls="task-diff-drawer"
            aria-label={isDrawerOpen ? 'Collapse changed files' : 'View changed files'}
          >
            <ChevronRightIcon className={styles.icon} data-expanded={isDrawerOpen} />
          </button>
          <div className={styles.branch}>
            <Link href={branchHref} className={styles.branchText}>
              {headRef}
            </Link>
            <CopyToClipboardButton
              textToCopy={headRef}
              variant="invisible"
              ariaLabel={`Copy branch name ${headRef}`}
              className={styles.copyIcon}
            />
          </div>
          {hasDiffs && (isSessionInProgress || !!pull || !onViewDiff) && (
            <DiffStats
              linesAdded={diffs.additions}
              linesDeleted={diffs.deletions}
              linesChanged={diffs.changes}
              hideSquares
            />
          )}
        </div>
        <div className={styles.actions}>
          {!isSessionInProgress && !pull && onViewDiff && (
            <Button size="small" onClick={() => onViewDiff?.()} aria-label="View diff">
              Diff
              {diffs && (diffs.additions > 0 || diffs.deletions > 0) && (
                <>
                  {' '}
                  {diffs.additions > 0 && <span className={styles.additions}>+{diffs.additions}</span>}
                  {diffs.additions > 0 && diffs.deletions > 0 && ' '}
                  {diffs.deletions > 0 && <span className={styles.deletions}>-{diffs.deletions}</span>}
                </>
              )}
            </Button>
          )}
          <PullRequestButton
            pullUrl={pull?.url}
            taskId={taskId}
            repoNwo={repoNwo}
            baseRef={baseRef}
            headRef={headRef}
            disabled={isSessionInProgress}
            onError={setPrError}
          />
        </div>
        {prError && (
          <Banner
            className={styles.prErrorBanner}
            variant="critical"
            title="Error creating pull request"
            hideTitle
            description={prError}
            onDismiss={() => setPrError(null)}
          />
        )}
      </div>
      <div id="task-diff-drawer" className={styles.diffContainer} data-expanded={isDrawerOpen}>
        <ActionList className={styles.diffList}>
          {diffs.files.map(diff => {
            const [directory, fileName] = getPathComponents(diff.path)
            return (
              <ActionList.Item
                key={diff.path}
                className={styles.diffItem}
                onSelect={() => {
                  if (!isSessionInProgress && !pull && onViewDiff) {
                    onViewDiff(diff.path)
                  } else if (pull?.url) {
                    window.open(`${pull.url}/files#diff-${diff.path_digest}`, '_blank')
                  } else {
                    window.open(`/${repoNwo}/blob/${headRef}/${diff.path}`, '_blank')
                  }
                }}
              >
                <ActionList.LeadingVisual>{getFileStatusIcon(diff.status)}</ActionList.LeadingVisual>
                <div className={styles.fileInfo}>
                  <span className={styles.fileName}>{fileName}</span>
                  <span className={styles.fileDirectory}>{directory}</span>
                </div>
                <ActionList.TrailingVisual>
                  <DiffStats
                    linesAdded={diff.additions}
                    linesDeleted={diff.deletions}
                    linesChanged={diff.changes}
                    hideSquares
                  />
                </ActionList.TrailingVisual>
              </ActionList.Item>
            )
          })}
        </ActionList>
      </div>
    </div>
  )
}
